
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';

const Pricing = () => {
  const [subscriptionLoading, setSubscriptionLoading] = useState<string | null>(null);
  const { user, subscription } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // If user already has active subscription, redirect to dashboard
    if (subscription?.subscribed) {
      navigate('/breeder-dashboard');
    }
  }, [subscription, navigate]);

  useEffect(() => {
    // Reset loading state when component mounts or when coming back from checkout
    setSubscriptionLoading(null);
  }, []);

  const handleSubscribe = async (planType: 'monthly' | 'yearly') => {
    setSubscriptionLoading(planType);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: {
          planType: planType
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.url) {
        // Open in same tab instead of new tab to avoid state issues
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: "There was an issue setting up your subscription. Please try again.",
        variant: "destructive",
      });
      // Reset loading state on error
      setSubscriptionLoading(null);
    }
  };

  const features = [
    "Unlimited animal listings",
    "Verified breeder profile", 
    "Direct buyer-seller messaging",
    "Location-based listings",
    "Create upcoming auctions",
    "Priority customer support",
    "EPD/EBV data display"
  ];

  return (
    <ProtectedRoute requiredUserType="breeder">
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-100 to-teal-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600">
              Select the perfect subscription for your needs
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 mb-12">
            {/* Monthly Plan */}
            <Card className="relative flex flex-col p-4">
              <CardHeader className="text-center pb-12">
                <CardTitle className="text-2xl mb-8">Monthly Plan</CardTitle>
                <div className="text-4xl font-bold text-green-700">
                  $30
                  <span className="text-lg font-normal text-gray-600">/month</span>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-3 flex-1">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full bg-green-700 hover:bg-green-800 mt-12"
                  size="lg"
                  onClick={() => handleSubscribe('monthly')}
                  disabled={subscriptionLoading !== null}
                >
                  {subscriptionLoading === 'monthly' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : null}
                  Get Started Monthly
                </Button>
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className="relative border-2 border-green-500 shadow-lg flex flex-col p-4">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white">
                BEST VALUE
              </Badge>
              <CardHeader className="text-center pb-12">
                <CardTitle className="text-2xl mb-8">Yearly Plan</CardTitle>
                <div className="text-4xl font-bold text-green-700">
                  $300
                  <span className="text-lg font-normal text-gray-600">/year</span>
                </div>
                <div className="text-sm text-green-600 font-medium mt-2">
                  Save $60 annually - 2 months free!
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-3 flex-1">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full bg-green-700 hover:bg-green-800 mt-12"
                  size="lg"
                  onClick={() => handleSubscribe('yearly')}
                  disabled={subscriptionLoading !== null}
                >
                  {subscriptionLoading === 'yearly' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : null}
                  Get Started Yearly
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/breeder-dashboard')}
            >
              I'll choose later
            </Button>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default Pricing;
